import videojs from "video.js";
import AdDisplayContainer from "./components/AdDisplayContainer";
import Ad from "./Ad";
import "./NullSourceHandler";
import {parseUrl, createUrl} from "@utilities/Url";
import Cache from "@utilities/Cache";

import "./Plugin.scss";

const logger = videojs.createLogger("BlueframeAds");

const _maxCorrelator = 9e15;

// Default opts for the plugin.
const defaults = {
	// Can be disabled on admin players
	enabled: true,
	// If this many milliseconds pass without loading an ad, disable ads
	adLoadTimeout: 3000,
	// Unique per page view
	correlator: null
};


/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 *
 * If you just want to play ads from a VMAP file during VOD content ("normal"
 * ads), favor the VideoJS-IMA plugin over this one. This plugin does not, on
 * its own, play any ads. Instead this is a wrapper around the IMA SDK which
 * provides a simple API for loading and playing ads from other plugins. This
 * way we can create "ad trigger" such as:
 *
 *  - Play ads when metadata is received
 *  - Play ads after a set timeout
 *  - Play ads when the user runs out of "ad credits"
 *  - Play ads on repeat before a broadcast starts
 *  - etc
 *
 * This is meant to be a flexible solution for creating new types of ad logic
 */
function BlueframeAds(options)
{
	const player = this;
	const opts = videojs.mergeOptions(defaults, options);

	if (!opts.enabled)
	{
		logger("Ads disabled");
		return;
	}

	let rdid = Cache.set(
		"rdid",
		Cache.get("rdid", Cache.guid())
	);

	if (opts.correlator === null)
	{
		opts.correlator = Math.floor(_maxCorrelator * Math.random());
	}

	const prerolls = [];
	const postrolls = [];

	function addPreroll(ad)
	{
		prerolls.push(ad);
	}

	function addPostroll(ad)
	{
		postrolls.push(ad);
	}

	const adContainer = new AdDisplayContainer(player);
	player.addChild(adContainer);

	// In order to ensure volume is sync'd across the content and ads, we'll
	// track the volume and muted state of the player within this plugin
	let volume = player.volume();
	let muted = player.muted();

	// We will also override several player methods to redirect them to the
	// content or ad appropriately
	const playerVolume = player.volume.bind(player);
	const playerMuted = player.muted.bind(player);
	const playerPlay = player.play.bind(player);
	const playerPause = player.pause.bind(player);
	const playerPaused = player.paused.bind(player);
	player.volume = setVolume;
	player.muted = setMuted;
	player.play = play;
	player.pause = pause;
	player.paused = paused;

	let _inAdBreak = false;
	let _prerollsStarted = false;
	let _prerollsPlayed = false;

	// During ads we will force the playbackRate to 1 so that metadata comes
	// as expected. We'll store the old value so that we can reset it when
	// the ad is over
	let preAdPlaybackRate;

	// We only want to enable seeking after ads if seeking was already enabled
	// before the ad started. Unfortunately there's no player.isSeekingEnabled()
	// method, so we have to listen for this event:
	let contentSeeking = true;
	player.on("seekingdisabled", () =>
	{
		if (!_inAdBreak) contentSeeking = false;
	});
	player.on("seekingenabled", () =>
	{
		if (!_inAdBreak) contentSeeking = true;
	});

	let preAdTracks = {};

	player.one("loadedmetadata", onReadyForPreroll);
	// player.one("complete", onReadyForPostroll);

	player.addClass("vjs-blueframe-ads");

	function setVolume(value)
	{
		if (typeof value === "undefined")
		{
			return volume;
		}

		// Hotkeys can cause volume to be >1 and <0. We dont want that.
		if (value > 1)
		{
			volume = 1;
		}
		else if (value < 0)
		{
			volume = 0;
		}
		else
		{
			volume = value;
		}

		if (_inAdBreak && player.ads.ad)
		{
			player.ads.ad.setVolume(volume);
		}
		else
		{
			playerVolume(volume);
		}

		// On iOS this is triggered automatically
		if (!videojs.browser.IS_IOS)
		{
			player.trigger("volumechange");
		}

		// ! VideoJS normally returns "undefined" here
		return volume;
	}

	function setMuted(value)
	{
		if (typeof value === "undefined")
		{
			return muted;
		}

		muted = value;

		if (_inAdBreak)
		{
			if (player.ads.ad)
			{
				player.ads.ad.setVolume(value ? 0 : volume);
			}
		}
		else
		{
			playerMuted(value);
		}

		// On iOS this is triggered automatically
		if (!videojs.browser.IS_IOS)
		{
			player.trigger("volumechange");
		}

		return muted;
	}

	let contentPausedDuringAd = false;

	function play()
	{
		if (_inAdBreak)
		{
			if (contentPausedDuringAd && _prerollsPlayed)
			{
				playerPlay();
			}
			if (player.ads.ad)
				return player.ads.ad.resume();
			return null;
		}
		if (!_prerollsPlayed)
		{
			// We're not in an adbreak YET, but we have prerolls...
			// That means we're still waiting to load those prerolls for
			// playback, and the video tried to autoplay
			const retVal = playerPause();
			if (!_prerollsStarted)
			{
				onReadyForPreroll();
			}
			return retVal;
		}

		return playerPlay();
	}

	function pause()
	{
		// If we are in an ad break but no ad is loaded then we dont want to pause the player.
		// Several things depend on the ad class functions to start/stop.
		if (_inAdBreak && !player.ads.ad)
		{
			return null;
		}

		if (_inAdBreak && player.ads.ad)
		{
			player.ads.ad.pause();
			contentPausedDuringAd = true;
		}

		return playerPause();
	}

	function paused()
	{
		if (_inAdBreak)
		{
			if (player.ads.ad)
				return player.ads.ad.paused();
			return null;
		}

		return playerPaused();
	}

	function startLinearAdMode()
	{
		logger("startLinearAdMode called");
		if (_inAdBreak)
		{
			logger.warn("Already in adbreak");
			return;
		}

		_inAdBreak = true;

		// Update the playbackRate to 1
		preAdPlaybackRate = player.playbackRate();
		player.playbackRate(1);

		logger.debug("Displaying AdContainer.");
		adContainer.show();

		if (contentSeeking)
		{
			// Tell the UI to hide seeking elements (skip button, seek bar, etc)
			player.trigger("seekingdisabled");
		}

		if (!videojs.browser.IS_IOS)
		{
			// On iOS we don't want to mute the content, as doing so would
			// also mute the ad since they share a video element
			logger.debug("This is NOT iOS. Muting the content.");
			playerMuted(true);

			// Similarly on iOS we don't want to black out the content, as doing
			// so would prevent fullscreening the player if you were to exit
			// fullscreen at any point
			// Black out the content
			// EDIT: removing the blackout for now. if ads fail then viewer is just looking at blackscreen.
			// adContainer.addClass("blackout");
		}

		// Remember which captions were selected at the start of the adbreak
		// Also disable captions during the ad (for performance)
		const tracks = player.textTracks();
		preAdTracks = {};
		for (let i = 0; i < tracks.length; i++)
		{
			preAdTracks[tracks[i].id] = tracks[i].mode;
			if (tracks[i].kind !== "metadata")
			{
				tracks[i].mode = "disabled";
			}
		}
	}

	function endLinearAdMode()
	{
		logger("endLinearAdMode called");

		if (!_inAdBreak)
		{
			logger.warn("Not in adbreak");
			return;
		}

		// Restore pre-ad captions settings
		const tracks = player.textTracks();
		for (let i = 0; i < tracks.length; i++)
		{
			tracks[i].mode = preAdTracks[tracks[i].id];
		}

		logger.debug("Hiding AdContainer.");
		adContainer.hide();

		// Revert the playbackRate
		player.playbackRate(preAdPlaybackRate);

		if (contentSeeking)
		{
			// Tell the UI to restore seek elements (skip button, seek bar, etc)
			player.trigger("seekingenabled");
		}

		// Unmute the content
		playerVolume(volume);
		playerMuted(muted);

		// UN-black out the content
		// adContainer.removeClass("blackout");

		_inAdBreak = false;
	}

	function inAdBreak()
	{
		return _inAdBreak;
	}

	function onReadyForPreroll()
	{
		_prerollsStarted = true;
		player.off("loadedmetadata", onReadyForPreroll);

		if (_prerollsPlayed) return;

		if (prerolls.length)
		{
			logger(`${prerolls.length} prerolls remain... Playing one`);
			const preroll = prerolls.shift();

			if (preroll.destroyed)
			{
				logger("Preroll is destroyed. Skipping...");
				onReadyForPreroll();
			}
			else
			{
				logger("Starting preroll");
				if (!preroll.loading)
				{
					preroll.load();
				}
				if (!_inAdBreak)
				{
					startLinearAdMode();
				}
				preroll.play();
				preroll.one(["complete", "destroyed"], onReadyForPreroll);

				// Preload the next pre-roll
				if (prerolls.length)
				{
					// Wait until the pre-roll is almost over before pre-loading
					// This way we aren't hammering the ad server too often
					preroll.one("thirdQuartile", prerolls[0].load.bind(prerolls[0]));
				}
			}
		}
		else
		{
			logger("No prerolls to play");
			if (_inAdBreak)
			{
				// We successfully played ads earlier. Tell the player we're done.
				endLinearAdMode();
			}

			_prerollsPlayed = true;

			// Now let's start the content!
			player.play();
		}
	}

	/*
	function onReadyForPostroll()
	{
		if (!opts.enabled)
		{
			player.trigger("nopostroll");
			return;
		}

		if (postrolls.length)
		{
			logger(`${postrolls.length} postrolls remain... Playing one`);
			const postroll = postrolls.shift();

			if (postroll.destroyed)
			{
				logger("Postroll is destroyed. Skipping...");
				setTimeout(onReadyForPostroll, 1);
			}
			else
			{
				logger("Starting postroll");
				if (!_inAdBreak)
				{
					startLinearAdMode();
				}
				postroll.play();
				postroll.on(["ended", "destroyed"], () =>
				{
					logger("Postroll ended");
					setTimeout(onReadyForPostroll, 1);
				});
			}
		}
		else
		{
			logger("No postrolls to play");
			if (_inAdBreak)
			{
				// We successfully played ads earlier. Tell the player we're done.
				endLinearAdMode();
			}
			else
			{
				// We have failed to play any ads. Tell the player there are none.
				player.trigger("nopostroll");
			}
		}
	}
	*/

	const adPlugin = {
		player: player,
		adContainer: adContainer,
		ad: null,
		addPreroll: addPreroll,
		addPostroll: addPostroll,
		startLinearAdMode: startLinearAdMode,
		endLinearAdMode: endLinearAdMode,
		inAdBreak: inAdBreak
	};

	/**
	 * @param {string} vastUrl
	 *        a URL which MUST point to XML VAST data referencing a single ad
	 */
	// eslint-disable-next-line no-unused-vars
	function load(vastUrl, adOptions = {})
	{
		if (!opts.enabled)
		{
			return null;
		}

		const urlDetails = parseUrl(vastUrl);

		// https://support.google.com/admanager/table/9749596?hl=en
		urlDetails.parameters.correlator = opts.correlator;
		urlDetails.parameters.env = "vp";
		// DoubleClick expects this parameter
		// Also DoubleClick only returns ads if the URL is a valid website that
		// they can access from their servers
		let url = window.location.href;
		if (url.indexOf("localhost") >= 0)
		{
			url = "https://blueframetech.com";
		}
		urlDetails.parameters.url = url;
		// AdX / AdSense expect these two parameters
		urlDetails.parameters.output = "xml_vast4";
		urlDetails.parameters.sdkv = "h.1.0.0";

		urlDetails.parameters.rdid = rdid;
		// navigator.doNotTrack is supposed to match the "DNT" HTTP header...
		// The "DNT" HTTP header can take a value of 0 or 1
		// However it can also not be set *at all*
		// If it's not set at all, the specification doesn't say what
		// navigator.doNotTrack is supposed to return. Firefox returns the
		// string "unspecified". Chrome returns null.
		urlDetails.parameters.is_lat =
			navigator.doNotTrack === "unspecified" ? 1 : navigator.doNotTrack || window.doNotTrack || 0;
		urlDetails.parameters.idtype = "idfa";

		const updatedUrl = createUrl(urlDetails);
		const newAd = new Ad(adPlugin, updatedUrl, adOptions);

		newAd.on("error", () =>
		{
			// Update the correlator, just in case
			opts.correlator = Math.floor(_maxCorrelator * Math.random());

			// The RDID is also used as a correlator
			rdid = Cache.set("rdid", Cache.guid());
		});

		return newAd;
	}

	adPlugin.load = load;

	player.blueframeAds = function () { return adPlugin; };
	player.ads = adPlugin;
}

// Register the plugin with video.js.
videojs.registerPlugin("blueframeAds", BlueframeAds);

export default BlueframeAds;
